/**
 * The composer's pending-attachment chip, with a preview of what is about to
 * be sent. A rep who just recorded a screen walkthrough (or picked a screenshot)
 * wants to see it before pressing Send — "📎 screen-recording-….webm · 3.1 MB"
 * says nothing about whether the clip caught the right tab (Nikhil 2026-09-14,
 * from his test of the client-side recorder; the CSP thread follows).
 *
 * upload() only gets {mediaId, filename, mimetype, size} back from the server,
 * so `with_preview` keeps an object URL of the local File on the pending state.
 * The URL lives exactly as long as this chip: the component revokes it on
 * unmount, which is how the attachment is cleared (✕, send, channel switch) and
 * how the thread goes away — no separate bookkeeping in the composer.
 *
 * Exercised by pending_attachment.test.js.
 */
import React, { useEffect, useRef } from 'react';

const fmt_size = (n) => {
    if (n == null) return '';
    if (n > 1048576) return (n / 1048576).toFixed(1) + ' MB';
    if (n > 1024) return Math.round(n / 1024) + ' KB';
    return n + ' B';
};

/**
 * 'video' | 'image' | null from a mimetype. The codecs suffix MediaRecorder
 * puts on its mimeType (`video/webm;codecs=vp8,opus`) is dropped before the
 * prefix check, so a recording previews whichever type it was stored with.
 */
export const preview_kind = (mimetype) => {
    const t = String(mimetype || '').split(';')[0].trim().toLowerCase();
    if (t.indexOf('video/') === 0) return 'video';
    if (t.indexOf('image/') === 0) return 'image';
    return null;
};

/**
 * The server's media row plus a preview source when the local File is
 * something we can show. Anything else (a PDF, a browser without object URLs)
 * comes back unchanged and the chip stays text-only.
 */
export const with_preview = (media, file) => {
    if (!media || !file) return media;
    const kind = preview_kind(file.type || media.mimetype);
    if (!kind || typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') return media;
    return { ...media, preview_kind: kind, preview_url: URL.createObjectURL(file) };
};

const PREVIEW_STYLE = { display: 'block', maxWidth: 260, maxHeight: 160, borderRadius: 4, marginBottom: 4, background: '#000' };
export const ENLARGE_TITLE = 'Enlarge';

/** The lightbox item for a pending preview — its key cannot collide with a posted `${message_id}:${idx}`. */
export const pending_zoom_item = (media) => ({ key: `pending:${media.mediaId}`, url: media.preview_url, filename: media.filename, kind: media.preview_kind });

/**
 * The browser's own fullscreen, for a thread that has no zoom viewer to hand.
 * Prefixed name for older Safari; a browser with neither just keeps the inline
 * preview (the native controls still carry Chrome's fullscreen button).
 */
const request_fullscreen = (el) => {
    if (!el) return;
    if (typeof el.requestFullscreen === 'function') el.requestFullscreen();
    else if (typeof el.webkitRequestFullscreen === 'function') el.webkitRequestFullscreen();
};

/**
 * The chip: preview (when there is one) above the "📎 filename · size ✕" line.
 * Videos must be enlargeable while viewing (Nikhil 2026-09-14): ⛶ — or a
 * click on an image thumbnail — opens the preview in the thread's zoom viewer
 * (`on_zoom`, the same one posted media use) and, without one, asks the
 * browser for fullscreen on the element. The video itself is not the click
 * target: with native `controls` a click there is play/pause, and the
 * controls already carry the browser's fullscreen button (never
 * controlsList="nofullscreen").
 */
export default function PendingAttachment({ media, on_clear, on_zoom }) {
    const url = media && media.preview_url;
    const el_ref = useRef(null);
    useEffect(() => {
        if (!url) return undefined;
        return () => { if (typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(url); };
    }, [url]);
    if (!media) return null;
    const enlarge = () => {
        if (on_zoom) on_zoom(pending_zoom_item(media));
        else request_fullscreen(el_ref.current);
    };
    return (
        <div style={{ fontSize: 11, color: '#7f8c8d', background: '#f6f8f9', borderRadius: 4, padding: '3px 8px', marginTop: 6 }}>
            {url && media.preview_kind === 'video' ? (
                // playsInline: an iPad rep should not be thrown into fullscreen by a preview.
                <video ref={el_ref} src={url} controls playsInline preload='metadata' style={PREVIEW_STYLE} />
            ) : url && media.preview_kind === 'image' ? (
                <img ref={el_ref} src={url} alt={media.filename || 'attachment'} onClick={enlarge} title={ENLARGE_TITLE}
                    style={{ ...PREVIEW_STYLE, background: undefined, cursor: 'zoom-in' }} />
            ) : null}
            📎 {media.filename} · {fmt_size(media.size)}
            {url ? (
                <span onClick={enlarge} title={ENLARGE_TITLE} style={{ cursor: 'pointer', marginLeft: 8, color: '#2f6fed' }}>⛶</span>
            ) : null}
            <span onClick={on_clear} title='Remove the attachment' style={{ cursor: 'pointer', float: 'right', color: '#e74c3c' }}>✕</span>
        </div>
    );
}
